import React, { useState } from 'react';
import { Button, message, Card, Dropdown, Menu, Empty, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { ReactComponent as MoreVertIcon } from '../../../../../images/icon-more-vert.svg';
import { ReactComponent as RefreshIcon } from '../../../../../images/icon-refresh.svg';

import { AddOrEditProperty, OperationType, DATA_PRODUCT, GLOSSARY_TERM, DOMAIN } from './AddOrEditProperty';
import { StyledTable } from '../../components/styled/StyledTable';
import { useEntityData, useRefetch } from '../../EntityContext';
import { EditColumn } from './Edit/EditColumn';
import ValuesColumn from './ValuesColumn';
import { useRbac } from '../../useRbac';
import NameColumn from './NameColumn';

import useStructuredProperties, { getApplicableStructuredPropertyRows } from './useStructuredProperties';
import { getFilteredCustomProperties, mapCustomPropertiesToPropertyRows } from './utils';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../../useEntityRegistry';
import { showDeleteModal } from '../../utils';
import { PropertyRow } from './types';

import { useDeleteDataProductPropertyMutation } from '../../../../../graphql/dataProduct.generated';
import { useDeleteGlossaryPropertyMutation } from '../../../../../graphql/glossary.generated';
import { useDeleteEntityPropertyMutation } from '../../../../../graphql/dataset.generated';
import { useDeleteDomainPropertyMutation } from '../../../../../graphql/domain.generated';
import {
    useGetApplicableStructuredPropertiesQuery,
    useRemoveStructuredPropertiesMutation,
} from '../../../../../graphql/structuredProperties.generated';

const ActionButtonContainer = styled.div`
    display: flex;
    justify-content: right;
    align-items: center;
`;

const StyledButton = styled(Button)`
    width: 100%;
`;

const StyledMenu = styled(Menu)`
    min-width: 148px !important;
    border-radius: 4px;
    padding: 0;

    .ant-dropdown-menu-item {
        color: #4c4e54;
        padding: 8px 12px !important;
    }

    .ant-dropdown-menu-item-disabled {
        pointer-events: auto !important;
    }
`;

const DEFINITION = 'Definition';

function getPropertiesRbac(
    canUpdate: boolean,
    canRemove: boolean,
    isReadOnly: boolean,
    messages: { update: string; remove: string },
    t: (key: string) => string,
) {
    const readOnlyTooltip = isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined;
    return {
        isUpdateDisabled: isReadOnly || !canUpdate,
        isRemoveDisabled: isReadOnly || !canRemove,
        updateTooltip: (!canUpdate ? messages.update : undefined) ?? readOnlyTooltip,
        removeTooltip: (!canRemove ? messages.remove : undefined) ?? readOnlyTooltip,
    };
}

export const PropertiesTab = () => {
    const { t } = useTranslation();
    const { entityData, entityType }: any = useEntityData();
    const entityRegistry = useEntityRegistry();
    const {
        canPerformStructuredPropertiesUpdate,
        canPerformStructuredPropertiesRemove,
        canPerformCustomPropertiesUpdate,
        canPerformCustomPropertiesRemove,
    } = useRbac();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly === true;

    const canStructuredUpdate = canPerformStructuredPropertiesUpdate(entityType);
    const canStructuredRemove = canPerformStructuredPropertiesRemove(entityType);
    const structured = getPropertiesRbac(
        canStructuredUpdate,
        canStructuredRemove,
        isReadOnly,
        { update: t('UNAUTHORISED_TOOLTIP'), remove: t('UNAUTHORISED_TOOLTIP') },
        t,
    );

    const canCustomUpdate = canPerformCustomPropertiesUpdate(entityType);
    const canCustomRemove = canPerformCustomPropertiesRemove(entityType);
    const custom = getPropertiesRbac(
        canCustomUpdate,
        canCustomRemove,
        isReadOnly,
        { update: t('UNAUTHORISED_TOOLTIP'), remove: t('UNAUTHORISED_TOOLTIP') },
        t,
    );

    const refetch = useRefetch();

    const [operationType, setOperationType] = useState(OperationType.ADD);
    const [isResetEnabled, setIsResetEnabled] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editModalData, setEditModalData] = useState({});

    const [resetStructuredPropertiesMutation] = useRemoveStructuredPropertiesMutation();
    const [deleteDataProductPropertyMutation] = useDeleteDataProductPropertyMutation();
    const [deleteGlossaryPropertyMutation] = useDeleteGlossaryPropertyMutation();
    const [deleteDomainPropertyMutation] = useDeleteDomainPropertyMutation();
    const [deleteEntityProperty] = useDeleteEntityPropertyMutation();

    const { data } = useGetApplicableStructuredPropertiesQuery({
        variables: {
            input: {
                entityUrn: entityData?.urn,
            },
        },
        fetchPolicy: 'cache-first',
        skip: !entityData?.urn,
        errorPolicy: 'all',
    });

    const applicableSp = data?.getApplicableStructuredProperties?.structuredProperties || [];

    const canAddProperty = [
        'DATASET',
        'CONTAINER',
        'DATA_PRODUCT',
        'DOMAIN',
        'CHART',
        'DASHBOARD',
        'DATA_FLOW',
        'DATA_JOB',
        'GLOSSARY_TERM',
        'GLOSSARY_NODE',
    ].includes(entityData?.type);

    const isEditable = [DATA_PRODUCT, DOMAIN].includes(entityData?.type);

    // checks if the url is valid or not
    function isValidUrl(string) {
        try {
            const url = new URL(string);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (err) {
            return false;
        }
    }

    const getCustomProperties = (properties) => {
        return properties?.filter((p) => {
            if (p.key === DEFINITION) {
                return isValidUrl(p.value);
            }
            return true;
        });
    };

    // Opens the add or edit modal based on OperationType.
    const openAddOrEditModal = (type, record) => {
        setIsModalVisible(true);
        setOperationType(type);

        if (type === OperationType.EDIT) {
            setEditModalData(record);
        }
    };

    const getDeleteMutationFunc = (type) => {
        switch (type) {
            case DATA_PRODUCT:
                return deleteDataProductPropertyMutation;
            case GLOSSARY_TERM:
                return deleteGlossaryPropertyMutation;
            case DOMAIN:
                return deleteDomainPropertyMutation;
            default:
                return deleteEntityProperty;
        }
    };

    const resetStructuredProperty = (urn: string) => {
        setIsResetEnabled(true);

        resetStructuredPropertiesMutation({
            variables: {
                input: {
                    assetUrn: entityData?.urn,
                    structuredPropertyUrns: [urn],
                },
            },
        })
            .then(({ errors }: any) => {
                if (!errors) {
                    message.success(t('Structured properties reset successfully.'));
                    refetch?.();
                }
            })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t('Failed to reset the structured properties: \n {{message}}', {
                        message: e.message || '',
                    }),
                    duration: 3,
                });
            })
            .finally(() => {
                setIsResetEnabled(false);
            });
    };

    // Deletes a glossary property.
    const deleteGlossaryProperty = (key) => {
        // Checks if the entity is a glossary term. If it is, it will delete the glossary property.
        const deleteMutation = getDeleteMutationFunc(entityData?.type);

        deleteMutation({
            variables: {
                input: {
                    urn: entityData?.urn || '',
                    key,
                },
            },
        })
            .then(({ errors }: any) => {
                if (!errors) {
                    message.success(t('Property deleted successfully.'));
                    refetch?.();
                }
            })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t('Failed to delete the property: \n {{message}}', { message: e.message || '' }),
                    duration: 3,
                });
            });
    };

    const onConfirmDelete = (record) => {
        const content = t('Are you sure you want to delete “{{key}}”? Please note that this action cannot be undone.', {
            key: record.key,
        });
        showDeleteModal(t('Property'), () => deleteGlossaryProperty(record.key), content);
    };

    const getPropertiesColumn = ({ isCustomProperty = true }): any => {
        return [
            {
                width: '35%',
                title: t('Name'),
                ariaLabel: t('Name'),
                defaultSortOrder: 'ascend',
                render: (propertyRow: PropertyRow) => <NameColumn propertyRow={propertyRow} />,
            },
            {
                title: t('Value'),
                render: (propertyRow: PropertyRow) => <ValuesColumn propertyRow={propertyRow} />,
            },
            {
                width: '210px',
                title:
                    canAddProperty && isCustomProperty ? (
                        <Tooltip title={custom.updateTooltip} overlayClassName="f-tooltip-white">
                            <StyledButton
                                type="text"
                                size="small"
                                className="f-btn-flat"
                                onClick={() => openAddOrEditModal(OperationType.ADD, '')}
                                disabled={custom.isUpdateDisabled}
                            >
                                <PlusOutlined />
                                <span className="f-btn-text-truncate" title={t('Add New Property')}>
                                    {t('Add New Property')}
                                </span>
                            </StyledButton>
                        </Tooltip>
                    ) : (
                        ''
                    ),
                render: (record: any) => {
                    if (record?.structuredProperty) {
                        const recordAllowedValue = Object.values(record.structuredProperty.definition.allowedValues);

                        const filteredAttachedValue = record.values.filter((v) =>
                            recordAllowedValue.some((av: any) => Object.values(av?.value)[0] === v.value),
                        );

                        return (
                            <ActionButtonContainer>
                                {record.values.length ? (
                                    <Tooltip title={structured.removeTooltip} overlayClassName="f-tooltip-white">
                                        <Button
                                            type="text"
                                            disabled={structured.isRemoveDisabled || isResetEnabled}
                                            className="icon-button mr-0"
                                            onClick={() => resetStructuredProperty(record.structuredProperty.urn)}
                                            icon={<RefreshIcon />}
                                        />
                                    </Tooltip>
                                ) : (
                                    <></>
                                )}

                                <EditColumn
                                    readOnly={structured.isUpdateDisabled}
                                    disabledTooltip={structured.updateTooltip}
                                    entityType={entityData?.type}
                                    structuredProperty={record.structuredProperty}
                                    values={filteredAttachedValue?.map((v) => v.value) || []}
                                />
                            </ActionButtonContainer>
                        );
                    }

                    const dropdownDisabled = custom.isUpdateDisabled && custom.isRemoveDisabled;
                    return isEditable || record?.isEditable ? (
                        <ActionButtonContainer>
                            <Tooltip
                                title={dropdownDisabled ? custom.updateTooltip ?? custom.removeTooltip : undefined}
                                overlayClassName="f-tooltip-white"
                            >
                                <span>
                                    <Dropdown
                                        trigger={['click']}
                                        disabled={dropdownDisabled}
                                        overlay={
                                            <StyledMenu>
                                                <Menu.Item
                                                    key="edit"
                                                    disabled={custom.isUpdateDisabled}
                                                    onClick={() => openAddOrEditModal(OperationType.EDIT, record)}
                                                >
                                                    <Tooltip
                                                        title={
                                                            custom.isUpdateDisabled ? custom.updateTooltip : undefined
                                                        }
                                                        overlayClassName="f-tooltip-white"
                                                        placement="left"
                                                    >
                                                        <span>{t('Edit Property')}</span>
                                                    </Tooltip>
                                                </Menu.Item>
                                                <Menu.Item
                                                    key="delete"
                                                    disabled={custom.isRemoveDisabled}
                                                    onClick={() => onConfirmDelete(record)}
                                                >
                                                    <Tooltip
                                                        title={
                                                            custom.isRemoveDisabled ? custom.removeTooltip : undefined
                                                        }
                                                        overlayClassName="f-tooltip-white"
                                                        placement="left"
                                                    >
                                                        <span>{t('Delete Property')}</span>
                                                    </Tooltip>
                                                </Menu.Item>
                                            </StyledMenu>
                                        }
                                    >
                                        <Button
                                            type="text"
                                            className="icon-button ma-0"
                                            icon={<MoreVertIcon />}
                                            disabled={dropdownDisabled}
                                        />
                                    </Dropdown>
                                </span>
                            </Tooltip>
                        </ActionButtonContainer>
                    ) : (
                        ''
                    );
                },
            },
        ];
    };

    const { structuredPropertyRows } = useStructuredProperties(entityRegistry, '');
    const customProperties = getFilteredCustomProperties('', entityData) || [];
    const customPropertyRows = mapCustomPropertiesToPropertyRows(getCustomProperties(customProperties), isEditable);

    // Combines the structured properties and custom properties.
    const getCombinedStructuredProperties = () => {
        const applicableProperties = getApplicableStructuredPropertyRows(applicableSp);

        const filteredProperties = applicableProperties?.filter((property: any) => {
            return !structuredPropertyRows?.some(
                (spRow: any) => spRow.structuredProperty.urn === property.structuredProperty.urn,
            );
        });

        return [...structuredPropertyRows, ...filteredProperties];
    };

    const structuredProperties = getCombinedStructuredProperties();

    return (
        <Card className="pa-4">
            <div className="f-text-big-content f-text-single-line f-color-dark-black-s100 mb-2">
                {t('Structured Properties')}
            </div>
            <div className="f-text-medium-content f-color-dark-black-s70 mb-3">
                {t('Properties that have already been created and assigned to the entity.')}
            </div>

            <div className="f-border-all f-rounded-corner-1 mb-4">
                <StyledTable
                    size="small"
                    pagination={false}
                    tableLayout="fixed"
                    rowKey="structuredProperties"
                    columns={getPropertiesColumn({ isCustomProperty: false })}
                    dataSource={structuredProperties}
                    locale={{
                        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Data')} />,
                    }}
                />
            </div>

            <div className="f-text-big-content f-text-single-line f-color-dark-black-s100 mb-2">
                {t('Custom Properties')}
            </div>
            <div className="f-text-medium-content f-color-dark-black-s70 mb-3">
                {t('Properties created and given values by the user.')}
            </div>

            <div className="f-border-all f-rounded-corner-1 overflow-hidden">
                <StyledTable
                    size="small"
                    pagination={false}
                    tableLayout="fixed"
                    rowKey="customProperties"
                    locale={{
                        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Data')} />,
                    }}
                    columns={getPropertiesColumn({ isCustomProperty: true })}
                    dataSource={customPropertyRows}
                />

                <AddOrEditProperty
                    urn={entityData?.urn}
                    type={entityData?.type}
                    isModalVisible={isModalVisible}
                    setIsModalVisible={setIsModalVisible}
                    editModalData={editModalData || ''}
                    operationType={operationType}
                    refetch={refetch}
                    customPropertyKeys={customPropertyRows.map((p) => p.displayName?.toLowerCase())}
                />
            </div>
        </Card>
    );
};
